import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  IconBell,
  IconUsersGroup,
  IconFileDescription,
  IconCircleCheck,
  IconArrowsExchange,
  IconSpeakerphone,
  type Icon as TablerIcon,
} from "@tabler/icons-react";
import {
  useNotificationsStore,
  unreadCount,
  type NotifKind,
} from "@/features/notifications/stores/notificationsStore";
import { cn } from "@/lib/utils/cn";

const kindMeta: Record<NotifKind, { icon: TablerIcon; tone: string }> = {
  broker_adopted: { icon: IconUsersGroup, tone: "bg-success-soft text-success" },
  proposal_created: { icon: IconFileDescription, tone: "bg-info-soft text-info" },
  proposal_accepted: { icon: IconCircleCheck, tone: "bg-brand-50 text-brand-600" },
  proposal_round: { icon: IconArrowsExchange, tone: "bg-warning-soft text-warning" },
  media_broadcast: { icon: IconSpeakerphone, tone: "bg-pink-soft text-pink-ink" },
};

export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const items = useNotificationsStore((s) => s.items);
  const markAllRead = useNotificationsStore((s) => s.markAllRead);
  const markRead = useNotificationsStore((s) => s.markRead);
  const navigate = useNavigate();
  const ref = useRef<HTMLDivElement>(null);

  const unread = unreadCount(items);

  useEffect(() => {
    if (!open) return;
    function onClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label={`Notificações${unread ? ` (${unread} não lidas)` : ""}`}
        aria-expanded={open}
        className="relative flex h-9 w-9 items-center justify-center rounded-lg text-ink-muted transition-colors hover:bg-bg hover:text-ink"
      >
        <IconBell size={19} />
        {unread > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-gradient-to-br from-pink to-orange px-1 text-[9px] font-bold text-white shadow-glow-pink">
            {unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-11 z-50 w-80 origin-top-right animate-fade-up overflow-hidden rounded-xl border border-border bg-white shadow-card">
          <div className="flex items-center justify-between border-b border-border-soft px-3.5 py-2.5">
            <span className="text-[13px] font-semibold">Notificações</span>
            {unread > 0 && (
              <button
                type="button"
                onClick={markAllRead}
                className="text-[11.5px] font-medium text-brand-600 hover:text-brand-700"
              >
                Marcar todas como lidas
              </button>
            )}
          </div>

          <ul className="max-h-80 divide-y divide-border-soft overflow-y-auto">
            {items.slice(0, 8).map((n) => {
              const meta = kindMeta[n.kind];
              const Icon = meta.icon;
              return (
                <li
                  key={n.id}
                  onClick={() => {
                    markRead(n.id);
                    setOpen(false);
                    if (n.href) navigate(n.href);
                  }}
                  className={cn(
                    "flex cursor-pointer items-start gap-2.5 px-3.5 py-2.5 transition-colors hover:bg-bg/60",
                    !n.read && "bg-brand-50/40",
                  )}
                >
                  <span
                    className={cn(
                      "mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg",
                      meta.tone,
                    )}
                  >
                    <Icon size={16} />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-[12.5px] leading-snug">{n.title}</p>
                    <p className="mt-0.5 text-[11px] text-ink-soft">{n.at}</p>
                  </div>
                  {!n.read && <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-brand-500" />}
                </li>
              );
            })}
          </ul>

          <Link
            to="/notifications"
            onClick={() => setOpen(false)}
            className="block border-t border-border-soft px-3.5 py-2 text-center text-[12px] font-medium text-brand-600 hover:bg-bg/50"
          >
            Ver todas
          </Link>
        </div>
      )}
    </div>
  );
}
